import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { API_URL, formatCurrency, formatDate, getStatusColor, QUOTE_STATUSES } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Skeleton } from '../components/ui/skeleton';
import { Calendar } from '../components/ui/calendar';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  Plus,
  Search,
  FileText,
  Edit2,
  Trash2,
  Eye,
  Download,
  CalendarIcon,
  ChevronRight,
  Copy,
} from 'lucide-react';

const QuoteCard = ({ quote, onView, onEdit, onDelete, onDownloadPDF, onDuplicate }) => {
  return (
    <Card
      className="card-hover cursor-pointer"
      onClick={() => onView(quote.id)}
      data-testid={`quote-card-${quote.id}`}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-medium">{quote.reference}</h3>
              <Badge variant="outline" className={getStatusColor(quote.status)}>
                {QUOTE_STATUSES.find((s) => s.value === quote.status)?.label || quote.status}
              </Badge>
            </div>
            <p className="text-sm text-muted-foreground">
              {quote.client_name || 'Sans client'}
            </p>
            {quote.start_date && quote.end_date && (
              <p className="text-xs text-muted-foreground mt-1">
                {formatDate(quote.start_date)} - {formatDate(quote.end_date)}
              </p>
            )}
          </div>

          <div className="text-right">
            <p className="text-lg font-bold">{formatCurrency(quote.total_ttc)}</p>
            <p className="text-xs text-muted-foreground">TTC</p>
          </div>
        </div>

        <div className="flex justify-between items-center mt-3 pt-3 border-t border-border">
          <span className="text-xs text-muted-foreground">
            Créé le {formatDate(quote.created_at)}
          </span>
          <div className="flex gap-1" onClick={(e) => e.stopPropagation()}>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onDuplicate(quote.id)}
              title="Dupliquer"
              data-testid={`duplicate-quote-${quote.id}`}
            >
              <Copy className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onDownloadPDF(quote.id)}
              data-testid={`download-pdf-${quote.id}`}
            >
              <Download className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onEdit(quote)}
              data-testid={`edit-quote-${quote.id}`}
            >
              <Edit2 className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-destructive"
              onClick={() => onDelete(quote.id)}
              data-testid={`delete-quote-${quote.id}`}
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const NewQuoteDialog = ({ open, onOpenChange, clients, initialStartDate, initialEndDate }) => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [clientId, setClientId] = useState('none');
  const [startDate, setStartDate] = useState(initialStartDate || null);
  const [endDate, setEndDate] = useState(initialEndDate || null);
  const [startPopoverOpen, setStartPopoverOpen] = useState(false);
  const [endPopoverOpen, setEndPopoverOpen] = useState(false);
  
  // Update dates when initial values change (from calendar selection)
  React.useEffect(() => {
    if (initialStartDate) setStartDate(initialStartDate);
    if (initialEndDate) setEndDate(initialEndDate);
  }, [initialStartDate, initialEndDate]);

  const createMutation = useMutation({
    mutationFn: async (data) => {
      console.log('[QuoteCreate] Sending payload:', JSON.stringify(data, null, 2));
      const res = await fetch(`${API_URL}/quotes`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ detail: 'Erreur inconnue' }));
        console.error('[QuoteCreate] Error response:', res.status, errorData);
        throw new Error(errorData.detail || `Erreur ${res.status}`);
      }
      return res.json();
    },
    onSuccess: (data) => {
      console.log('[QuoteCreate] Success:', data);
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success(`Devis ${data.reference} créé`);
      onOpenChange(false);
      // Reset form
      setClientId('none');
      setStartDate(null);
      setEndDate(null);
      navigate(`/quotes/${data.id}`);
    },
    onError: (error) => {
      console.error('[QuoteCreate] Mutation error:', error);
      toast.error(error.message || 'Erreur lors de la création');
    },
  });

  //Fonction utilitaire pour éviter le bug du J-1 dû aux fuseaux horaires : on force les heures à midi (12:00) pour que la date envoyée soit correcte même si l'utilisateur est dans un fuseau horaire différent
  const toSafeISOString = (date) => {
    if (!date) return null;
    const safeDate = new Date(date);
    // On force l'heure à 12:00:00 (Midi) LOCALE
    safeDate.setHours(12, 0, 0, 0); 
    return safeDate.toISOString();
};

  const handleSubmit = (e) => {
    e.preventDefault();
    const payload = {
      client_id: clientId === 'none' ? null : clientId,
      items: [],
      start_date: startDate ? toSafeISOString(startDate) : null,
      end_date: endDate ? toSafeISOString(endDate) : null,
    };
    console.log('[QuoteCreate] Form submit, payload:', payload);
    createMutation.mutate(payload);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-heading">Nouveau devis</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label>Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="quote-client-select">
                <SelectValue placeholder="Sélectionner un client" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sans client</SelectItem>
                {clients?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name} {c.company && `(${c.company})`}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date début</Label>
              <Popover open={startPopoverOpen} onOpenChange={setStartPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="quote-start-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDate ? format(startDate, 'dd/MM/yyyy') : 'Choisir'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={startDate}
                    onSelect={(date) => {
                      setStartDate(date);
                      setStartPopoverOpen(false);
                    }}
                    locale={fr}
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Date fin</Label>
              <Popover open={endPopoverOpen} onOpenChange={setEndPopoverOpen}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left font-normal"
                    data-testid="quote-end-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDate ? format(endDate, 'dd/MM/yyyy') : 'Choisir'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={endDate}
                    onSelect={(date) => {
                      setEndDate(date);
                      setEndPopoverOpen(false);
                    }}
                    locale={fr}
                    disabled={(date) => startDate && date < startDate}
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Annuler
            </Button>
            <Button type="submit" data-testid="create-quote-btn">
              Créer le devis
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const QuotesPage = () => {
  const { token } = useAuthStore();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Get initial dates from URL params (from calendar redirect)
  const [initialStartDate, setInitialStartDate] = useState(null);
  const [initialEndDate, setInitialEndDate] = useState(null);
  
  useEffect(() => {
    const startParam = searchParams.get('start');
    const endParam = searchParams.get('end');
    if (startParam) {
      setInitialStartDate(new Date(startParam));
      setDialogOpen(true);
    }
    if (endParam) {
      setInitialEndDate(new Date(endParam));
    }
    // Clear URL params after reading
    if (startParam || endParam) {
      setSearchParams({});
    }
  }, []);

  const { data: quotes = [], isLoading } = useQuery({
    queryKey: ['quotes'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/quotes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch quotes');
      return res.json();
    },
  });

  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => {
      const res = await fetch(`${API_URL}/clients`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to fetch clients');
      return res.json();
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/quotes/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to delete quote');
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success('Devis supprimé');
    },
    onError: () => toast.error('Erreur lors de la suppression'),
  });

  const duplicateMutation = useMutation({
    mutationFn: async (id) => {
      const res = await fetch(`${API_URL}/quotes/${id}/duplicate`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to duplicate quote');
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['quotes'] });
      toast.success(`Devis dupliqué: ${data.reference}`);
      navigate(`/quotes/${data.id}`);
    },
    onError: () => toast.error('Erreur lors de la duplication'),
  });

  const handleDownloadPDF = async (id) => {
    try {
      const res = await fetch(`${API_URL}/quotes/${id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Failed to generate PDF');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `devis-${id}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF téléchargé');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const filteredQuotes = quotes.filter((q) => {
    const matchesSearch =
      q.reference.toLowerCase().includes(search.toLowerCase()) ||
      (q.client_name || '').toLowerCase().includes(search.toLowerCase());
    const matchesStatus = statusFilter === 'all' || q.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="space-y-6" data-testid="quotes-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold">Devis</h1>
          <p className="text-muted-foreground">Gérez vos devis et propositions</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} data-testid="add-quote-btn">
          <Plus className="w-4 h-4 mr-2" /> Nouveau devis
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un devis..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
            data-testid="quote-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full md:w-48" data-testid="status-filter">
            <SelectValue placeholder="Statut" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les statuts</SelectItem>
            {QUOTE_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Quotes List */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <Card key={i}>
              <CardContent className="p-4">
                <div className="flex justify-between">
                  <div className="space-y-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-24" />
                  </div>
                  <Skeleton className="h-8 w-20" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredQuotes.length > 0 ? (
        <div className="space-y-3">
          {filteredQuotes.map((quote) => (
            <QuoteCard
              key={quote.id}
              quote={quote}
              onView={(id) => navigate(`/quotes/${id}`)}
              onEdit={(q) => navigate(`/quotes/${q.id}`)}
              onDelete={(id) => {
                if (window.confirm('Supprimer ce devis ?')) {
                  deleteMutation.mutate(id);
                }
              }}
              onDownloadPDF={handleDownloadPDF}
              onDuplicate={(id) => duplicateMutation.mutate(id)}
            />
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-12 text-center">
            <FileText className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
            <h3 className="font-heading font-semibold mb-2">Aucun devis trouvé</h3>
            <p className="text-muted-foreground mb-4">
              {search ? 'Essayez avec d\'autres termes' : 'Créez votre premier devis'}
            </p>
            <Button onClick={() => setDialogOpen(true)}>
              <Plus className="w-4 h-4 mr-2" /> Créer un devis
            </Button>
          </CardContent>
        </Card>
      )}

      <NewQuoteDialog
        open={dialogOpen}
        onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            // Reset initial dates when dialog closes
            setInitialStartDate(null);
            setInitialEndDate(null);
          }
        }}
        clients={clients}
        initialStartDate={initialStartDate}
        initialEndDate={initialEndDate}
      />
    </div>
  );
};

export default QuotesPage;
